import { CheckCircle } from 'lucide-react';
import { palette, admin, urgency as urg, transition } from '../../styles/tokens';
import type { AttentionItem } from '../../types';

function getUrgencyColor(type: string): string {
  if (type === 'overdue_invoice' || type === 'coverage_gap') return urg.critical;
  if (type === 'overtime_warning' || type === 'maintenance_due') return urg.warning;
  return urg.info;
}

function ActionRow({ item, onClick }: { item: AttentionItem; onClick: () => void }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 4px', borderBottom: `0.5px solid ${admin.border}`,
      cursor: 'pointer', transition: `background ${transition.fast}`,
    }}
      onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{
        width: urg.dotSize, height: urg.dotSize, borderRadius: urg.dotSize / 2,
        background: getUrgencyColor(item.type), flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: admin.header }}>{item.text}</div>
        {item.meta && <div style={{ fontSize: 11, color: admin.caption, marginTop: 1 }}>{item.meta}</div>}
      </div>
      <span style={{
        fontSize: 11, fontWeight: 500, color: palette.accent,
        whiteSpace: 'nowrap',
      }}>{item.action} &rarr;</span>
    </div>
  );
}

interface AttentionListProps {
  items: AttentionItem[];
  onItemClick?: (item: AttentionItem) => void;
}

export function AttentionList({ items, onItemClick }: AttentionListProps) {
  if (items.length === 0) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '24px 0', justifyContent: 'center',
      }}>
        <CheckCircle size={16} color={palette.green} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: admin.header }}>All clear</div>
          <div style={{ fontSize: 11, color: admin.caption }}>Nothing needs your attention right now.</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {items.map(item => (
        <ActionRow key={item.id} item={item} onClick={() => onItemClick?.(item)} />
      ))}
    </div>
  );
}
